/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { announce, clearAnnouncer } from '@react-aria/live-announcer';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { ChevronPreviousIcon } from '@coursera/cds-icons';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  root: css`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;

    .settings-page-header {
      display: flex;
      z-index: 1;
      align-items: center;
      height: 54px;
      border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);

      button {
        margin-left: var(--cds-spacing-150);
      }
    }

    .back-button-container,
    .empty-spacer {
      flex: 1;
    }

    a {
      color: inherit !important;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }

      &:focus {
        outline: 1px solid var(--cds-color-emphasis-quaternary-background-strong);
        outline-offset: 2px;
        border-radius: var(--cds-border-radius-50);
      }
    }

    ${breakpoints.up('md')} {
      [data-course-frame-2026] & {
        display: flex;
        flex-direction: column;

        .settings-page-header {
          flex-shrink: 0;
          height: auto;
          padding: var(--cds-spacing-150) 0;
        }
      }
    }
  `,
  content: css`
    padding: var(--cds-spacing-200);
    flex: 1;
    min-height: 0;
    overflow: auto;

    ${breakpoints.up('md')} {
      [data-course-frame-2026] & {
        flex: 1;
        min-height: 0;
        height: auto;
      }
    }
  `,
  footer: css`
    flex-shrink: 0;
    padding: var(--cds-spacing-200);

    ${breakpoints.up('md')} {
      [data-course-frame-2026] & {
        flex-shrink: 0;
        height: auto;
        position: static;
      }
    }
  `,
};

type Props = {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const SettingsPage = (props: Props): React.ReactElement => {
  const { onBack, title, children, footer } = props;

  useEffect(() => {
    // announces current page title on mount
    clearAnnouncer('assertive');
    announce(title, 'assertive');
  }, [title]);

  return (
    <div className="coach-settings-page-container" css={styles.root}>
      <div className="settings-page-header">
        <div className="back-button-container">
          <CoachIconButton
            onClick={onBack}
            size="small"
            icon={<ChevronPreviousIcon size="small" color="default" />}
            tooltip={_t('Go back')}
            aria-label={_t('Back to Coursera AI Chat Settings')}
          />
        </div>

        <div>
          <Typography2 variant="subtitleMedium" component="h2">
            {title}
          </Typography2>
        </div>

        {/* allows centering title while keeping back button left-aligned */}
        <div className="empty-spacer" />
      </div>

      <div css={styles.content}>{children}</div>

      <div css={styles.footer}>{footer}</div>
    </div>
  );
};

export default SettingsPage;
